import axiosClient from '@clients/axiosClient';

type IGetFlexControls = { accountId: number };

type IGetCustomerFlexControls = { customerId: number; accountId: number };

type CreateAccountFlexControl = {
    accountId: number;
    dataToSubmit: AccountFlexControlsPayload;
};

type UpdateAccountFlexControl = {
    accountId: number;
    flexControlId: string;
    dataToSubmit: AccountFlexControlsPayload;
};

type UpdateCustomerFlexControl = {
    accountId: number;
    customerId: number;
    flexControlId: string;
    dataToSubmit: CustomerFlexControlsPayload;
};

type CreateCustomerFlexControl = {
    customerId: number;
    accountId: number;
    dataToSubmit: CustomerFlexControlsPayload;
};

export type FlexControlsResponse = FlexControl[];

export type CustomerFlexControlsResponse = CustomerFlexControl[];

export type CreateCustomerFlexControlsResponse = {
    id: string;
    type: string;
    rule_reference_id: string;
    account_id: number;
    customer_id: number;
    name: string;
    max_limit: number;
    limit_duration: string;
    reset_datetime: string;
    available_limit: number;
    card_ids: number[];
    conditions: FlexControlCondition[];
    active: boolean;
};

export type CreateAccountFlexControlsResponse = {
    id: string;
    type: string;
    name: string;
    processing_codes: number[];
    max_limit: number;
    limit_duration: string;
    reset_datetime: string;
    deny_code: string;
    active: boolean;
};

export type UpdateAccountFlexControlsResponse =
    CreateAccountFlexControlsResponse;

export type UpdateCustomerFlexControlsResponse =
    CreateCustomerFlexControlsResponse;

export interface CustomerFlexControl extends FlexControl {
    rule_reference_id: string;
    account_id: number;
    customer_id: number;
    card_ids: number[];
}

export type FlexControl = {
    id: string;
    rule_reference_id: string;
    type: string;
    name: string;
    beneficiary_id: string;
    override_controls: string[];
    description: string;
    deny_code: string;
    processing_codes: string[];
    country_codes: string[];
    currency_code: string;
    time_zone: string;
    max_limit: number;
    available_limit: number;
    conditions: FlexControlCondition[];
    limit_duration: string;
    reset_period: {
        month_day: number;
        week_day: string;
        time: string;
    };
    reset_datetime: string;
    active: boolean;
    customized: boolean;
};

type FlexControlCondition = {
    id?: string;
    attribute: string;
    operator: string;
    value: string;
};

export type FlexControlsShared = {
    name: string;
    active?: boolean;
    beneficiary_id?: string;
    currency_code?: string;
    deny_code: string;
    limit_duration?: string;
    time?: string;
    time_zone?: string;
    type: string;
    week_day?: string;
    month_day?: string;
    conditions: FlexControlCondition[];
    reset_period?: {
        month_day?: number;
        week_day?: string;
        time?: string;
    };
};
export interface AccountFlexControlsFormInputs extends FlexControlsShared {
    max_limit?: string;
    override_controls?: string;
    processing_codes?: string;
}

export interface AccountFlexControlsPayload extends FlexControlsShared {
    max_limit?: number;
    override_controls?: string[];
    processing_codes?: string[];
}

export interface CustomerFlexControlsFormInputs extends FlexControlsShared {
    account_id: number;
    max_limit?: string;
    override_controls?: string;
    processing_codes?: string;
    card_ids?: number[];
}

export interface CustomerFlexControlsPayload extends FlexControlsShared {
    account_id?: number;
    max_limit?: number;
    override_controls?: string[];
    processing_codes?: string[];
    card_ids?: number[];
}

const flexControls = () => {
    const client = axiosClient();

    return {
        async getFlexControls({ accountId }: IGetFlexControls) {
            const { data } = await client.get<FlexControlsResponse>(
                `rules/v1/accounts/${accountId}/flex-controls`,
                {
                    params: { accountId },
                },
            );
            return data;
        },

        async getCustomerFlexControls({
            customerId,
            accountId,
        }: IGetCustomerFlexControls) {
            const { data } = await client.get<CustomerFlexControlsResponse>(
                `rules/v1/customers/${customerId}/flex-controls`,
                { headers: { 'x-account-id': accountId } },
            );
            return data;
        },

        async postCreateAccountFlexControl({
            accountId,
            dataToSubmit,
        }: CreateAccountFlexControl) {
            const { data } =
                await client.post<CreateAccountFlexControlsResponse>(
                    `/rules/v1/accounts/${accountId}/flex-controls`,
                    {
                        ...dataToSubmit,
                    },
                    { headers: { 'x-account-id': accountId } },
                );
            return data;
        },
        async patchUpdateAccountFlexControl({
            accountId,
            flexControlId,
            dataToSubmit,
        }: UpdateAccountFlexControl) {
            const { data } =
                await client.patch<UpdateAccountFlexControlsResponse>(
                    `/rules/v1/accounts/${accountId}/flex-controls/${flexControlId}`,
                    {
                        ...dataToSubmit,
                    },
                );
            return data;
        },
        async postCreateCustomerFlexControl({
            accountId,
            customerId,
            dataToSubmit,
        }: CreateCustomerFlexControl) {
            const { data } =
                await client.post<CreateCustomerFlexControlsResponse>(
                    `/rules/v1/customers/${customerId}/flex-controls`,
                    {
                        ...dataToSubmit,
                    },
                    { headers: { 'x-account-id': accountId } },
                );
            return data;
        },
        async patchUpdateCustomerFlexControl({
            customerId,
            flexControlId,
            dataToSubmit,
            accountId,
        }: UpdateCustomerFlexControl) {
            const { data } =
                await client.patch<UpdateCustomerFlexControlsResponse>(
                    `/rules/v1/customers/${customerId}/flex-controls/${flexControlId}`,
                    {
                        ...dataToSubmit,
                    },
                    { headers: { 'x-account-id': accountId } },
                );
            return data;
        },
    };
};

export default flexControls;
